import type {CSSProperties, ReactNode} from 'react';
import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import type {Rank} from '@types';
import RankText from './RankText';
import {UserAvatar, type AvatarUser} from './UserAvatar';

export type IdentityUser = AvatarUser & {
    id?: number;
    name: string;
    rank?: Rank | null;
    displayRank?: Rank | null;
};

export type UserIdentityProps = {
    user?: IdentityUser | null;
    anonymous?: boolean;
    anonymousLabel?: string;
    showAvatar?: boolean;
    avatarSize?: number;
    showRank?: boolean;
    secondary?: ReactNode;
    layout?: 'stacked' | 'inline';
    to?: string;
    target?: string;
    className?: string;
    avatarClassName?: string;
    avatarStyle?: CSSProperties;
    textClassName?: string;
};

export const UserIdentity = ({
    user,
    anonymous = false,
    anonymousLabel,
    showAvatar = true,
    avatarSize = 30,
    showRank = false,
    secondary,
    layout = 'stacked',
    to,
    target,
    className = '',
    avatarClassName,
    avatarStyle,
    textClassName = '',
}: UserIdentityProps) => {
    const {t} = useTranslation();
    const identity = anonymous ? null : user;
    const name =
        identity?.name ?? anonymousLabel ?? t('attempts.anonymous_user');
    const rank = identity?.displayRank ?? identity?.rank;
    const detail = identity
        ? (secondary ?? (showRank && rank ? <RankText rank={rank} /> : null))
        : null;
    const classes =
        `user-identity user-identity--${layout} ${className}`.trim();
    const content = (
        <>
            {showAvatar && (
                <UserAvatar
                    user={identity}
                    size={avatarSize}
                    className={avatarClassName}
                    style={avatarStyle}
                />
            )}
            <span className={`user-identity-info ${textClassName}`.trim()}>
                <span className="user-identity-name">{name}</span>
                {detail != null && (
                    <span className="user-identity-secondary">{detail}</span>
                )}
            </span>
        </>
    );

    return identity ? (
        <Link
            className={classes}
            to={to ?? `/users/${identity.name}`}
            state={identity.id == null ? undefined : {userId: identity.id}}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            title={name}>
            {content}
        </Link>
    ) : (
        <span className={classes}>{content}</span>
    );
};
